// The 표 (grid) screen's data, without I/O (design 02 12.1): a workbook's
// sheets, the cells as the server has them, defined names, and this
// device's edits waiting to be sent. GridStore (store.ts) does the I/O
// around it (the API, the socket's changes, the calc worker).
//
// Rules:
// - The server keeps inputs and styles only; values are computed here.
// - A cell from the server replaces the one held when it is newer: a later
//   updated_at, or the same with a version not lower (an emptied cell comes
//   with version 0, so the time decides). Cells of an older structure
//   version than the sheet's are dropped: the sheet is read again after a
//   structure change anyway.
// - Edits are shown at once (an overlay over the server's cells) and sent
//   in order. Each batch carries the structure version it was made on. When
//   rows or columns of the sheet are inserted or deleted (sheet.structure),
//   the waiting edits move the same way (edits of deleted cells are
//   dropped); their formula texts are not rewritten.
import type { Id, TensorCell, TensorChange, TensorName, TensorSheet, TensorStructureChange, TensorStyle } from '../api'

export const GRID_ROWS = 1_048_576
export const GRID_COLS = 16_384
/** the server's limit of cells in one PUT */
export const CELLS_PER_PUT = 5000

export type Style = TensorStyle
export type Axis = 'row' | 'col'

export const cellKey = (row: number, col: number): number => row * GRID_COLS + col
export const keyRow = (k: number): number => Math.floor(k / GRID_COLS)
export const keyCol = (k: number): number => k % GRID_COLS

/** A cell as the server has it. */
export interface SCell {
  input: string
  style: Style | null
  version: number
  at: number
}

/** What a person changed in a cell; style undefined leaves it as it is, null clears it. */
export interface CellEdit {
  row: number
  col: number
  input: string
  style?: Style | null
}

export type OpStatus = 'queued' | 'sending'

export type GridOp =
  | { id: number; type: 'cells'; sheetId: Id; sv: number; edits: CellEdit[]; status: OpStatus }
  | { id: number; type: 'structure'; sheetId: Id; sv: number; axis: Axis; at: number; count: number; status: OpStatus }

/** A cell as the screen shows it (the server's, with my waiting edits). */
export interface ShownCell {
  input: string
  style: Style | null
  pending: boolean
}

export function newerCell(cur: SCell | undefined, inc: Pick<TensorCell, 'version' | 'updated_at'>): boolean {
  if (!cur) return true
  return inc.updated_at > cur.at || (inc.updated_at === cur.at && inc.version >= cur.version)
}

/** Where a cell goes when rows or columns are inserted (count > 0) or deleted (count < 0); null: deleted. */
export function shiftPos(row: number, col: number, axis: Axis, at: number, count: number): { row: number; col: number } | null {
  const p = axis === 'row' ? row : col
  let q = p
  if (count > 0) {
    if (p >= at) q = p + count
  } else {
    const n = -count
    if (p >= at && p < at + n) return null
    if (p >= at + n) q = p - n
  }
  const max = axis === 'row' ? GRID_ROWS : GRID_COLS
  if (q >= max) return null
  return axis === 'row' ? { row: q, col } : { row, col: q }
}

/** Where a waiting row/column insert or delete goes when another one came
    first (same axis): moved like a cell, or null when the two overlap
    (rows deleted under it, or rows inserted inside a range it deletes) and
    it cannot keep its meaning: it is dropped, the person is told. */
export function shiftStructure(
  op: { axis: Axis; at: number; count: number },
  ch: { axis: Axis; at: number; count: number },
): { at: number; count: number } | null {
  if (op.axis !== ch.axis) return { at: op.at, count: op.count }
  if (ch.count > 0) {
    // lines inserted before ch.at
    if (op.count > 0) return { at: ch.at <= op.at ? op.at + ch.count : op.at, count: op.count }
    const lo = op.at
    const hi = op.at - op.count // it deletes [lo, hi)
    if (ch.at <= lo) return { at: op.at + ch.count, count: op.count }
    if (ch.at >= hi) return { at: op.at, count: op.count }
    return null // inserted into the lines it deletes: they are someone else's now
  }
  // lines [dlo, dhi) deleted
  const dlo = ch.at
  const dhi = ch.at - ch.count
  const n = dhi - dlo
  if (op.count > 0) {
    if (dhi <= op.at) return { at: op.at - n, count: op.count }
    if (dlo >= op.at) return { at: op.at, count: op.count }
    return { at: dlo, count: op.count } // its place was deleted: where the gap closed
  }
  const lo = op.at
  const hi = op.at - op.count
  if (dhi <= lo) return { at: op.at - n, count: op.count }
  if (dlo >= hi) return { at: op.at, count: op.count }
  return null // some of the lines it meant are gone already
}

/** The waiting ops moved over a structure change of their sheet. Ops made on
    the version before it move (cells: their positions; structure: their
    place, or dropped when they overlap) and take its version; a structure
    op being sent is the change itself or will be answered stale, so it is
    left. */
export function shiftOps(ops: GridOp[], ch: Pick<TensorStructureChange, 'sheet_id' | 'structure_version' | 'axis' | 'at' | 'count'>): GridOp[] {
  const out: GridOp[] = []
  for (const o of ops) {
    if (o.sheetId !== ch.sheet_id || o.sv !== ch.structure_version - 1) {
      out.push(o)
      continue
    }
    if (o.type === 'structure') {
      if (o.status === 'sending') {
        out.push(o)
        continue
      }
      const moved = shiftStructure(o, ch)
      if (moved) out.push({ ...o, ...moved, sv: ch.structure_version })
      continue
    }
    const edits: CellEdit[] = []
    for (const e of o.edits) {
      const p = shiftPos(e.row, e.col, ch.axis, ch.at, ch.count)
      if (p) edits.push({ ...e, row: p.row, col: p.col })
    }
    out.push({ ...o, sv: ch.structure_version, edits })
  }
  return out
}

/** The style after a patch: keys set to undefined/false/"" go away; null when nothing is left. */
export function mergeStyle(base: Style | null | undefined, patch: Partial<Record<keyof Style, unknown>>): Style | null {
  const out: Record<string, unknown> = { ...(base ?? {}) }
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined || v === false || v === '' || v === null) delete out[k]
    else out[k] = v
  }
  return Object.keys(out).length ? (out as Style) : null
}

export function sameStyle(a: Style | null | undefined, b: Style | null | undefined): boolean {
  const x = a ?? {}
  const y = b ?? {}
  const keys = new Set([...Object.keys(x), ...Object.keys(y)]) as Set<keyof Style>
  for (const k of keys) if (x[k] !== y[k]) return false
  return true
}

export interface ChangeEffect {
  /** sheets whose cells must be read again (rows or columns moved) */
  reload: Id[]
  /** sheets that came or went or were renamed */
  sheets: boolean
  names: boolean
  /** waiting row/column changes dropped because someone else's overlapped */
  dropped?: number
}

/** The data of one workbook's grid. Mutable: GridStore owns it and tells
    the screen when it changed. */
export class GridModel {
  sheets = new Map<Id, TensorSheet>()
  cells = new Map<Id, Map<number, SCell>>()
  names = new Map<Id, TensorName>()
  ops: GridOp[] = []
  /** cells whose shown input may have changed, per sheet (for the worker) */
  dirty = new Map<Id, Set<number>>()
  /** the structure version of the cells read, per sheet */
  loadedSv = new Map<Id, number>()
  private overlay = new Map<Id, Map<number, ShownCell>>()
  private nextOp = 1

  constructor(readonly workbookId: Id) {}

  /** Sheets in order (position, then id). */
  sheetList(): TensorSheet[] {
    return [...this.sheets.values()].sort((a, b) => a.position - b.position || a.id - b.id)
  }

  sheetByName(name: string): TensorSheet | undefined {
    const low = name.toLowerCase()
    for (const s of this.sheets.values()) if (s.name.toLowerCase() === low) return s
    return undefined
  }

  nameList(): TensorName[] {
    return [...this.names.values()].sort((a, b) => a.name.localeCompare(b.name))
  }

  private markDirty(sheetId: Id, k: number): void {
    let d = this.dirty.get(sheetId)
    if (!d) this.dirty.set(sheetId, (d = new Set()))
    d.add(k)
  }

  /** Takes the dirty cells (and forgets them). */
  takeDirty(): Map<Id, Set<number>> {
    const d = this.dirty
    this.dirty = new Map()
    return d
  }

  /** A sheet from the server; false when an equal or newer one is here. */
  putSheet(s: TensorSheet): boolean {
    const cur = this.sheets.get(s.id)
    if (cur && (cur.version > s.version || (cur.version === s.version && cur.updated_at >= s.updated_at))) {
      // the structure version may still be newer (a structure answer)
      if (s.structure_version > cur.structure_version) this.sheets.set(s.id, { ...cur, structure_version: s.structure_version })
      return false
    }
    if (s.deleted) {
      this.removeSheet(s.id)
      return !!cur
    }
    const sv = Math.max(s.structure_version, cur?.structure_version ?? 0)
    this.sheets.set(s.id, { ...s, structure_version: sv })
    if (!this.cells.has(s.id)) this.cells.set(s.id, new Map())
    return true
  }

  removeSheet(id: Id): void {
    const cells = this.cells.get(id)
    if (cells) for (const k of cells.keys()) this.markDirty(id, k)
    this.sheets.delete(id)
    this.cells.delete(id)
    this.overlay.delete(id)
    this.loadedSv.delete(id)
    this.ops = this.ops.filter((o) => o.sheetId !== id)
  }

  putName(n: TensorName): boolean {
    const cur = this.names.get(n.id)
    if (cur && (cur.version > n.version || (cur.version === n.version && cur.updated_at >= n.updated_at))) return false
    if (n.deleted) return this.names.delete(n.id)
    this.names.set(n.id, n)
    return true
  }

  /** Cells from the server (a change or an answer). Older structure versions are dropped. */
  putCells(sheetId: Id, sv: number, cells: TensorCell[]): number {
    const sheet = this.sheets.get(sheetId)
    const map = this.cells.get(sheetId)
    if (!sheet || !map || sv < sheet.structure_version || sv < (this.loadedSv.get(sheetId) ?? 0)) return 0
    let n = 0
    for (const c of cells) {
      const k = cellKey(c.row, c.col)
      if (!newerCell(map.get(k), c)) continue
      if (c.version === 0 && c.input === '' && c.style === null) map.delete(k)
      else map.set(k, { input: c.input, style: c.style ?? null, version: c.version, at: c.updated_at })
      this.markDirty(sheetId, k)
      n++
    }
    return n
  }

  /** All of a sheet's cells, read again. */
  replaceCells(sheetId: Id, sv: number, cells: TensorCell[]): void {
    const old = this.cells.get(sheetId)
    if (old) for (const k of old.keys()) this.markDirty(sheetId, k)
    const map = new Map<number, SCell>()
    for (const c of cells) {
      const k = cellKey(c.row, c.col)
      map.set(k, { input: c.input, style: c.style ?? null, version: c.version, at: c.updated_at })
      this.markDirty(sheetId, k)
    }
    this.cells.set(sheetId, map)
    this.loadedSv.set(sheetId, sv)
    const sheet = this.sheets.get(sheetId)
    if (sheet && sv > sheet.structure_version) this.sheets.set(sheetId, { ...sheet, structure_version: sv })
    this.rebuildOverlay(sheetId)
  }

  /** A change from the socket (or catching up). */
  applyChange(c: TensorChange): ChangeEffect {
    const fx: ChangeEffect = { reload: [], sheets: false, names: false }
    if (c.workbook_id !== this.workbookId) return fx
    switch (c.type) {
      case 'sheet.updated': {
        const s = c.data as TensorSheet
        const before = this.sheets.get(s.id)
        if (this.putSheet(s)) fx.sheets = !before || before.name !== s.name || before.position !== s.position || !!s.deleted
        if (!before && !s.deleted && this.sheets.has(s.id)) fx.reload.push(s.id)
        break
      }
      case 'sheet.structure': {
        const ch = c.data as TensorStructureChange
        const sheet = this.sheets.get(ch.sheet_id)
        if (!sheet) break
        if (ch.structure_version > (this.loadedSv.get(ch.sheet_id) ?? 0)) {
          const before = this.ops.length
          this.ops = shiftOps(this.ops, ch)
          if (this.ops.length < before) fx.dropped = before - this.ops.length
          if (ch.structure_version > sheet.structure_version)
            this.sheets.set(sheet.id, { ...sheet, structure_version: ch.structure_version })
          this.rebuildOverlay(ch.sheet_id, true)
          fx.reload.push(ch.sheet_id)
        }
        break
      }
      case 'cells.updated': {
        const ch = c.data as { sheet_id: Id; structure_version: number; cells: TensorCell[] }
        this.putCells(ch.sheet_id, ch.structure_version, ch.cells)
        break
      }
      case 'name.updated':
        fx.names = this.putName(c.data as TensorName)
        break
    }
    return fx
  }

  // ---- my edits ----

  /** Edits made now on the sheet: shown at once, sent later (in order). */
  edit(sheetId: Id, edits: CellEdit[]): GridOp | null {
    const sheet = this.sheets.get(sheetId)
    if (!sheet || edits.length === 0) return null
    const last = this.ops[this.ops.length - 1]
    let op: GridOp
    if (
      last &&
      last.type === 'cells' &&
      last.status === 'queued' &&
      last.sheetId === sheetId &&
      last.sv === sheet.structure_version &&
      last.edits.length + edits.length <= CELLS_PER_PUT
    ) {
      op = { ...last, edits: [...last.edits, ...edits] }
      this.ops = [...this.ops.slice(0, -1), op]
    } else {
      op = { id: this.nextOp++, type: 'cells', sheetId, sv: sheet.structure_version, edits, status: 'queued' }
      this.ops = [...this.ops, op]
    }
    this.rebuildOverlay(sheetId, true)
    return op
  }

  /** Inserting or deleting rows or columns, after the edits before it. */
  structure(sheetId: Id, axis: Axis, at: number, count: number): GridOp | null {
    const sheet = this.sheets.get(sheetId)
    if (!sheet || count === 0) return null
    const op: GridOp = { id: this.nextOp++, type: 'structure', sheetId, sv: sheet.structure_version, axis, at, count, status: 'queued' }
    this.ops = [...this.ops, op]
    return op
  }

  setStatus(id: number, status: OpStatus): void {
    this.ops = this.ops.map((o) => (o.id === id ? { ...o, status } : o))
  }

  /** All waiting ops back to queued (after a failed send). */
  requeue(): void {
    this.ops = this.ops.map((o) => (o.status === 'sending' ? { ...o, status: 'queued' } : o))
  }

  /** An op is done (sent, or given up): it stops being shown. */
  finish(id: number): void {
    const op = this.ops.find((o) => o.id === id)
    if (!op) return
    this.ops = this.ops.filter((o) => o.id !== id)
    if (op.type === 'cells') this.rebuildOverlay(op.sheetId, true)
  }

  /** Drops the waiting edits of a sheet; how many cells they had. */
  dropOps(sheetId: Id): number {
    let n = 0
    for (const o of this.ops) if (o.sheetId === sheetId && o.type === 'cells') n += o.edits.length
    this.ops = this.ops.filter((o) => o.sheetId !== sheetId)
    this.rebuildOverlay(sheetId, true)
    return n
  }

  pendingOf(sheetId: Id): GridOp[] {
    return this.ops.filter((o) => o.sheetId === sheetId)
  }

  private rebuildOverlay(sheetId: Id, mark = false): void {
    const old = this.overlay.get(sheetId)
    const next = new Map<number, ShownCell>()
    const server = this.cells.get(sheetId)
    for (const o of this.ops) {
      if (o.sheetId !== sheetId || o.type !== 'cells') continue
      for (const e of o.edits) {
        const k = cellKey(e.row, e.col)
        const prev = next.get(k)
        const style = e.style !== undefined ? e.style : prev ? prev.style : (server?.get(k)?.style ?? null)
        next.set(k, { input: e.input, style, pending: true })
      }
    }
    if (next.size) this.overlay.set(sheetId, next)
    else this.overlay.delete(sheetId)
    if (mark) {
      if (old) for (const k of old.keys()) this.markDirty(sheetId, k)
      for (const k of next.keys()) this.markDirty(sheetId, k)
    }
  }

  /** The cell as shown (undefined: empty). */
  shown(sheetId: Id, row: number, col: number): ShownCell | undefined {
    const k = cellKey(row, col)
    const o = this.overlay.get(sheetId)?.get(k)
    if (o) return o
    const c = this.cells.get(sheetId)?.get(k)
    return c ? { input: c.input, style: c.style, pending: false } : undefined
  }

  shownInput(sheetId: Id, k: number): string {
    const o = this.overlay.get(sheetId)?.get(k)
    if (o) return o.input
    return this.cells.get(sheetId)?.get(k)?.input ?? ''
  }

  /** Every cell with an input or a style (keys), the overlay's too. */
  keysOf(sheetId: Id): Set<number> {
    const out = new Set<number>(this.cells.get(sheetId)?.keys() ?? [])
    for (const k of this.overlay.get(sheetId)?.keys() ?? []) out.add(k)
    return out
  }

  /** The last used row and column (-1: none). */
  usedRange(sheetId: Id): { rows: number; cols: number } {
    let rows = -1
    let cols = -1
    for (const k of this.keysOf(sheetId)) {
      const r = keyRow(k)
      const c = keyCol(k)
      if (r > rows) rows = r
      if (c > cols) cols = c
    }
    return { rows, cols }
  }
}
